interface SessionResponse {
    sessionId: string;
    uplinkUrl: string;
    baseUrl: string;
    scripts: {
        bash: string;
        powershell: string;
    };
}

interface TestResult {
    testId: string;
    name: string;
    description: string | null;
    status: string;
    durationMs: number;
    details: string | null;
    errorMessage: string | null;
}

interface SessionStatus {
    sessionId: string;
    state: string;
    createdAt: string;
    testResults: TestResult[];
}

interface CategoryGroup {
    name: string;
    prefix: string;
    results: TestResult[];
}

const CATEGORIES: { prefix: string; name: string }[] = [
    { prefix: 'DEVICE-', name: 'Device Service' },
    { prefix: 'MEDIA-', name: 'Media Profiles' },
    { prefix: 'REC-', name: 'Recording' },
    { prefix: 'EVENTS-', name: 'Event Subscription' },
    { prefix: 'EDGE-', name: 'Edge Recording' },
    { prefix: 'WEBRTC-', name: 'WebRTC' },
    { prefix: 'PTZ-', name: 'PTZ' },
];

interface SpecRef {
    spec: string;     // Human-readable spec name, e.g. "ONVIF Recording Service Spec"
    section: string;  // Section number and title, e.g. "9.1 GetRecordingConfiguration"
    url: string;      // Link to spec PDF or landing page
}

/**
 * Maps step IDs to their ONVIF specification reference.
 * Populated incrementally — any unmapped step silently omits the spec line.
 * To add an entry: verify the section number in the actual PDF, then add:
 *   'REC-001': { spec: 'ONVIF Recording Service Spec', section: '9.x Title', url: 'https://www.onvif.org/specs/srv/recording/ONVIF-Recording-Service-Spec.pdf' }
 *
 * Base URLs by category:
 *   DEVICE, EVENTS : https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf
 *   MEDIA          : https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf
 *   REC, EDGE      : https://www.onvif.org/specs/srv/recording/ONVIF-Recording-Service-Spec.pdf
 *   PTZ            : https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf
 *   WEBRTC         : https://www.onvif.org/specs/srv/webrtc/ONVIF-WebRTC-Spec.pdf
 */
const SPEC_REFS: Record<string, SpecRef> = {
    // DEVICE — ONVIF Core Specification
    'DEVICE-001': { spec: 'ONVIF Core Specification', section: '8.1.2.3 GetServiceCapabilities', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'DEVICE-002': { spec: 'ONVIF Core Specification', section: '8.1.2.4 GetCapabilities', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'DEVICE-003': { spec: 'ONVIF Core Specification', section: '8.2.10 GetNetworkInterfaces', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'DEVICE-004': { spec: 'ONVIF Core Specification', section: '8.8.3 GetStorageConfigurations', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'DEVICE-005': { spec: 'ONVIF Core Specification', section: '8.3.6 GetSystemDateAndTime', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'DEVICE-006': { spec: 'ONVIF Core Specification', section: '8.4.3 Get users', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },

    // MEDIA — ONVIF Media2 Service Specification
    'MEDIA-001': { spec: 'ONVIF Media2 Service Specification', section: '5.1.2 GetProfiles', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'MEDIA-002': { spec: 'ONVIF Media2 Service Specification', section: '5.3.2 Get<entity>Configurations', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'MEDIA-003': { spec: 'ONVIF Media2 Service Specification', section: '5.3.2 Get<entity>Configurations', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'MEDIA-004': { spec: 'ONVIF Media2 Service Specification', section: '5.3.4 Get<entity>ConfigurationOptions', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'MEDIA-005': { spec: 'ONVIF Media2 Service Specification', section: '5.1.2 GetProfiles', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'MEDIA-006': { spec: 'ONVIF Media2 Service Specification', section: '5.3.3 Set<entity>Configuration', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },

    // REC — Recording Control Service Specification (capability gate and recording ops)
    //       Core Specification (storage configuration)
    //       Media2 Service Specification (encoder/source configuration changes)
    'REC-001': { spec: 'ONVIF Recording Control Service Specification', section: '5.25 GetServiceCapabilities', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'REC-002': { spec: 'ONVIF Core Specification', section: '8.8.4 CreateStorageConfiguration', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'REC-003': { spec: 'ONVIF Recording Control Service Specification', section: '5.4 CreateRecording', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'REC-004': { spec: 'ONVIF Recording Control Service Specification', section: '5.13 CreateRecordingJob', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'REC-005': { spec: 'ONVIF Recording Control Service Specification', section: '5.6 GetRecordings', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'REC-006': { spec: 'ONVIF Recording Control Service Specification', section: '5.15 GetRecordingJobs', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'REC-008': { spec: 'ONVIF Media2 Service Specification', section: '5.3.3 Set<entity>Configuration', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'REC-010': { spec: 'ONVIF Media2 Service Specification', section: '5.3.4 Get<entity>ConfigurationOptions', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'REC-012': { spec: 'ONVIF Media2 Service Specification', section: '5.3.3 Set<entity>Configuration', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'REC-013': { spec: 'ONVIF Media2 Service Specification', section: '5.3.2 Get<entity>Configurations', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },

    // EVENTS — ONVIF Core Specification
    'EVENTS-001': { spec: 'ONVIF Core Specification', section: '8.1.2.4 GetCapabilities', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'EVENTS-002': { spec: 'ONVIF Core Specification', section: '9.7 Get event properties', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'EVENTS-003': { spec: 'ONVIF Core Specification', section: '9.1.1 Create pull point subscription', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'EVENTS-004': { spec: 'ONVIF Core Specification', section: '9.1.2 Pull messages', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'EVENTS-005': { spec: 'ONVIF Core Specification', section: '9.1.4 Unsubscribe', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },

    // EDGE — Recording Control Service Specification (capability gate, recording, export)
    //        Core Specification (storage configuration, events)
    'EDGE-001': { spec: 'ONVIF Recording Control Service Specification', section: '5.25 GetServiceCapabilities', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'EDGE-002': { spec: 'ONVIF Core Specification', section: '8.8.4 CreateStorageConfiguration', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'EDGE-003': { spec: 'ONVIF Recording Control Service Specification', section: '5.4 CreateRecording', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'EDGE-004': { spec: 'ONVIF Recording Control Service Specification', section: '5.13 CreateRecordingJob', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'EDGE-006': { spec: 'ONVIF Core Specification', section: '9.1.1 Create pull point subscription', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },
    'EDGE-007': { spec: 'ONVIF Recording Control Service Specification', section: '5.21 ExportRecordedData', url: 'https://www.onvif.org/specs/srv/rec/ONVIF-RecordingControl-Service-Spec.pdf' },
    'EDGE-008': { spec: 'ONVIF Core Specification', section: '9.1.2 Pull messages', url: 'https://www.onvif.org/specs/core/ONVIF-Core-Specification.pdf' },

    // WEBRTC — Media2 Service Specification (capabilities, profiles, WebRTC config)
    //          ONVIF WebRTC Specification (signaling, streaming)
    'WEBRTC-001': { spec: 'ONVIF Media2 Service Specification', section: '5.14 GetServiceCapabilities', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'WEBRTC-002': { spec: 'ONVIF Media2 Service Specification', section: '5.1.2 GetProfiles', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'WEBRTC-003': { spec: 'ONVIF Media2 Service Specification', section: '5.11.2 GetWebRTCConfigurations', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'WEBRTC-004': { spec: 'ONVIF Media2 Service Specification', section: '5.11.3 SetWebRTCConfigurations', url: 'https://www.onvif.org/specs/srv/media/ONVIF-Media2-Service-Spec.pdf' },
    'WEBRTC-005': { spec: 'ONVIF WebRTC Specification', section: '5 Signaling Protocol', url: 'https://www.onvif.org/specs/stream/ONVIF-WebRTC-Spec.pdf' },
    'WEBRTC-006': { spec: 'ONVIF WebRTC Specification', section: '6 WebRTC Usage', url: 'https://www.onvif.org/specs/stream/ONVIF-WebRTC-Spec.pdf' },

    // PTZ — ONVIF PTZ Service Specification
    'PTZ-001': { spec: 'ONVIF PTZ Service Specification', section: '5.1.2 GetNodes', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-002': { spec: 'ONVIF PTZ Service Specification', section: '5.2.2 GetConfigurations', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-003': { spec: 'ONVIF PTZ Service Specification', section: '5.4.2 GetPresets', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-004': { spec: 'ONVIF PTZ Service Specification', section: '5.4.1 SetPreset', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-005': { spec: 'ONVIF PTZ Service Specification', section: '5.4.3 GotoPreset', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-006': { spec: 'ONVIF PTZ Service Specification', section: '5.3.3 ContinuousMove', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-007': { spec: 'ONVIF PTZ Service Specification', section: '5.3.1 AbsoluteMove', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-008': { spec: 'ONVIF PTZ Service Specification', section: '5.3.5 Stop', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
    'PTZ-009': { spec: 'ONVIF PTZ Service Specification', section: '5.3.2 RelativeMove', url: 'https://www.onvif.org/specs/srv/ptz/ONVIF-PTZ-Service-Spec.pdf' },
};

class ConformanceApp {
    private currentSession: SessionResponse | null = null;
    private ws: WebSocket | null = null;
    private wsReconnectTimer: number | null = null;
    private sessionDone = false;
    private expandedCategories = new Set<string>();
    private scriptFormat: 'bash' | 'powershell' = 'powershell';
    private webRtcPeer: WebRtcBrowserPeer | null = null;

    constructor() {
        this.renderCategoryCheckboxes();
        this.bindEvents();
    }

    private renderCategoryCheckboxes(): void {
        const container = document.getElementById('category-checkboxes');
        if (!container) return;

        for (const cat of CATEGORIES) {
            const id = `cat-${cat.prefix.replace(/-$/, '').toLowerCase()}`;
            const label = document.createElement('label');
            label.className = 'category-checkbox-label';
            label.htmlFor = id;

            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.id = id;
            cb.value = cat.prefix.replace(/-$/, ''); // e.g. "DEVICE"
            cb.name = 'test-category';
            cb.checked = true;

            label.appendChild(cb);
            label.appendChild(document.createTextNode(cat.name));
            container.appendChild(label);
        }
    }

    private getSelectedTestIds(): string[] {
        return Array.from(
            document.querySelectorAll<HTMLInputElement>('input[name="test-category"]:checked')
        ).map(cb => cb.value);
    }

    private bindEvents(): void {
        document.getElementById('new-session-btn')?.addEventListener('click', () => this.createSession());
        document.getElementById('select-all-btn')?.addEventListener('click', () => {
            document.querySelectorAll<HTMLInputElement>('input[name="test-category"]')
                .forEach(cb => { cb.checked = true; });
        });
        document.getElementById('deselect-all-btn')?.addEventListener('click', () => {
            document.querySelectorAll<HTMLInputElement>('input[name="test-category"]')
                .forEach(cb => { cb.checked = false; });
        });
    }

    private async createSession(): Promise<void> {
        const deviceIp = (document.getElementById('device-ip') as HTMLInputElement)?.value;
        const username = (document.getElementById('device-user') as HTMLInputElement)?.value;
        const password = (document.getElementById('device-pass') as HTMLInputElement)?.value;

        if (!deviceIp || !username || !password) {
            alert('Please fill in all device credentials');
            return;
        }

        const selectedTests = this.getSelectedTestIds();
        if (selectedTests.length === 0) {
            alert('Please select at least one test category');
            return;
        }

        // Tear down any existing connections
        this.webRtcPeer?.stop();
        this.webRtcPeer = null;
        this.disconnectWebSocket();
        this.sessionDone = false;
        this.expandedCategories.clear();

        // Pass null when everything is selected (backend treats null as "all")
        const allCategories = CATEGORIES.length;
        const selectedTestsPayload = selectedTests.length === allCategories ? null : selectedTests;

        try {
            const response = await fetch('/api/session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deviceIp, username, password, selectedTests: selectedTestsPayload })
            });

            if (!response.ok) {
                throw new Error(`Failed to create session: ${response.statusText}`);
            }

            this.currentSession = await response.json();
            this.showSessionInfo();
            this.connectWebSocket();

            // Show/hide live video panel and start browser WebRTC peer if needed
            const webRtcIncluded = selectedTests.length === CATEGORIES.length || selectedTests.includes('WEBRTC');
            const videoContainer = document.getElementById('webrtc-video-container');
            const videoEl = document.getElementById('webrtc-video') as HTMLVideoElement | null;
            if (videoContainer) videoContainer.classList.toggle('hidden', !webRtcIncluded);
            if (videoEl) videoEl.srcObject = null;

            if (webRtcIncluded) {
                this.webRtcPeer = new WebRtcBrowserPeer(this.currentSession!.sessionId);
                this.webRtcPeer.start();
            }
        } catch (error) {
            console.error('Error creating session:', error);
            alert('Failed to create session. Please try again.');
        }
    }

    private showSessionInfo(): void {
        if (!this.currentSession) return;

        // Show device IP in header
        const deviceIp = (document.getElementById('device-ip') as HTMLInputElement)?.value ?? '';
        const username = (document.getElementById('device-user') as HTMLInputElement)?.value ?? '';
        const selectedNames = this.getSelectedCategoryNames();

        const headerDeviceEl = document.getElementById('header-device');
        if (headerDeviceEl) headerDeviceEl.textContent = deviceIp;

        // Populate and show slim bar
        const slimBar = document.getElementById('slim-bar');
        if (slimBar) {
            slimBar.innerHTML = `
                <span class="slim-bar-ip">${this.escapeHtml(deviceIp)}</span>
                <span class="slim-bar-sep">·</span>
                <span class="slim-bar-info">${this.escapeHtml(username)}</span>
                <span class="slim-bar-sep">·</span>
                <span class="slim-bar-info">${this.escapeHtml(selectedNames)}</span>
                <button class="slim-bar-edit">Edit ›</button>
            `;
        }

        document.getElementById('results-section')?.classList.remove('hidden');

        this.renderInlineScriptPanel();

        const resultsContainer = document.getElementById('results-container');
        if (resultsContainer) {
            resultsContainer.innerHTML = '<div class="waiting-message">Waiting for device to connect...</div>';
        }

        this.setWsIndicator('connecting');
    }

    private getSelectedCategoryNames(): string {
        const checked = Array.from(
            document.querySelectorAll<HTMLInputElement>('input[name="test-category"]:checked')
        );
        if (checked.length === CATEGORIES.length) return 'All categories';
        return checked.map(cb => {
            const cat = CATEGORIES.find(c => c.prefix.replace(/-$/, '') === cb.value);
            return cat ? cat.name : cb.value;
        }).join(', ');
    }

    private renderInlineScriptPanel(): void {
        if (!this.currentSession) return;
        const card = document.getElementById('session-control');
        if (!card) return;

        // Remove any previously rendered panel
        card.querySelector('.inline-script-panel')?.remove();

        const panel = document.createElement('div');
        panel.className = 'inline-script-panel';
        panel.innerHTML = `
            <div class="inline-script-meta">
                <span class="pill-label">Session</span>
                <code>${this.escapeHtml(this.currentSession.sessionId)}</code>
                <span class="pill-label" style="margin-left:12px">Uplink</span>
                <code>${this.escapeHtml(this.currentSession.uplinkUrl)}</code>
            </div>
            <div class="inline-script-bar">
                <div class="format-tabs">
                    <label class="format-tab active-tab" data-fmt="powershell">
                        <input type="radio" name="script-format" value="powershell" checked hidden>PowerShell
                    </label>
                    <label class="format-tab" data-fmt="bash">
                        <input type="radio" name="script-format" value="bash" hidden>Bash
                    </label>
                </div>
                <button class="btn btn-sm btn-copy-script">Copy</button>
            </div>
            <div class="script-container">
                <pre id="full-script"></pre>
            </div>
        `;
        card.appendChild(panel);

        // Wire events on the freshly created elements
        panel.querySelectorAll('input[name="script-format"]').forEach(radio => {
            radio.addEventListener('change', (e) => {
                this.scriptFormat = (e.target as HTMLInputElement).value as 'bash' | 'powershell';
                this.updateScriptDisplay();
                // Update active-tab class
                panel.querySelectorAll('.format-tab').forEach(t =>
                    t.classList.toggle('active-tab',
                        (t as HTMLElement).dataset['fmt'] === this.scriptFormat));
            });
        });
        panel.querySelector('.btn-copy-script')?.addEventListener('click', () => this.copyScript());

        this.updateScriptDisplay();
    }

    private updateScriptDisplay(): void {
        if (!this.currentSession) return;

        const scriptEl = document.getElementById('full-script');
        if (scriptEl) {
            scriptEl.textContent = this.scriptFormat === 'bash'
                ? this.currentSession.scripts.bash
                : this.currentSession.scripts.powershell;
        }
    }

    private async copyScript(): Promise<void> {
        const scriptEl = document.getElementById('full-script');
        const copyBtn = document.querySelector<HTMLButtonElement>('.btn-copy-script');

        if (scriptEl && copyBtn) {
            try {
                await navigator.clipboard.writeText(scriptEl.textContent || '');
                const originalText = copyBtn.textContent;
                copyBtn.textContent = 'Copied!';
                setTimeout(() => {
                    copyBtn.textContent = originalText;
                }, 2000);
            } catch (error) {
                console.error('Failed to copy:', error);
            }
        }
    }

    // ── WebSocket ───────────────────────────────────────────────────────────

    private connectWebSocket(): void {
        if (!this.currentSession || this.sessionDone) return;

        if (this.wsReconnectTimer !== null) {
            clearTimeout(this.wsReconnectTimer);
            this.wsReconnectTimer = null;
        }

        const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
        const url = `${protocol}://${location.host}/api/session/${this.currentSession.sessionId}/ws`;

        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
            this.setWsIndicator('live');
        };

        this.ws.onmessage = (event: MessageEvent) => {
            const status: SessionStatus = JSON.parse(event.data as string);
            this.handleStatusUpdate(status);
        };

        this.ws.onclose = () => {
            this.setWsIndicator('reconnecting');
            if (!this.sessionDone) {
                this.wsReconnectTimer = window.setTimeout(() => this.connectWebSocket(), 2000);
            } else {
                this.setWsIndicator('done');
            }
        };

        this.ws.onerror = () => {
            // onclose fires right after onerror — reconnect handled there
        };
    }

    private disconnectWebSocket(): void {
        if (this.wsReconnectTimer !== null) {
            clearTimeout(this.wsReconnectTimer);
            this.wsReconnectTimer = null;
        }
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close();
            this.ws = null;
        }
    }

    private handleStatusUpdate(status: SessionStatus): void {
        if (status.testResults.length > 0) {
            this.updateResultsDisplay(status.testResults);
            this.updateSummary(status.testResults);
        }

        // Collapse device config + script once testing is underway
        if (status.state !== 'Waiting') {
            document.getElementById('session-control')?.classList.add('hidden');
            document.getElementById('slim-bar')?.classList.remove('hidden');
            document.getElementById('header-device')?.classList.remove('hidden');
        } else {
            document.getElementById('session-control')?.classList.remove('hidden');
            document.getElementById('slim-bar')?.classList.add('hidden');
            document.getElementById('header-device')?.classList.add('hidden');
        }

        if (status.state === 'Completed' || status.state === 'Failed') {
            this.sessionDone = true;
            this.setWsIndicator('done');
            this.disconnectWebSocket();
        }
    }

    // ── WS status indicator ─────────────────────────────────────────────────

    private setWsIndicator(state: 'connecting' | 'live' | 'reconnecting' | 'done'): void {
        const indicator = document.getElementById('ws-indicator');
        const text = document.getElementById('ws-indicator-text');
        if (!indicator || !text) return;

        indicator.classList.remove('hidden', 'ws-live', 'ws-reconnecting', 'ws-done', 'ws-connecting');

        switch (state) {
            case 'connecting':
                indicator.classList.add('ws-connecting');
                text.textContent = 'Connecting...';
                break;
            case 'live':
                indicator.classList.add('ws-live');
                text.textContent = 'Live';
                break;
            case 'reconnecting':
                indicator.classList.add('ws-reconnecting');
                text.textContent = 'Reconnecting...';
                break;
            case 'done':
                indicator.classList.add('ws-done');
                text.textContent = 'Done';
                break;
        }
    }

    // ── Stats row ────────────────────────────────────────────────────────────

    private updateSummary(results: TestResult[]): void {
        const passed  = results.filter(r => r.status === 'Passed').length;
        const failed  = results.filter(r => r.status === 'Failed').length;
        const skipped = results.filter(r => r.status === 'Skipped').length;
        const total   = results.length;

        // Render or update the stats row — prepend before #results-container
        const section = document.getElementById('results-section');
        const container = document.getElementById('results-container');
        if (!section || !container) return;

        let statsRow = section.querySelector<HTMLElement>('.stats-row');
        if (!statsRow) {
            statsRow = document.createElement('div');
            statsRow.className = 'stats-row';
            section.insertBefore(statsRow, container);
        }

        statsRow.innerHTML = `
            <div class="stat-tile passed"><div class="stat-tile-num">${passed}</div><div class="stat-tile-label">Passed</div></div>
            <div class="stat-tile failed"><div class="stat-tile-num">${failed}</div><div class="stat-tile-label">Failed</div></div>
            <div class="stat-tile skipped"><div class="stat-tile-num">${skipped}</div><div class="stat-tile-label">Skipped</div></div>
            <div class="stat-tile total"><div class="stat-tile-num">${total}</div><div class="stat-tile-label">Total</div></div>
        `;
    }

    // ── Results table ───────────────────────────────────────────────────────

    private groupResultsByCategory(results: TestResult[]): CategoryGroup[] {
        const groups: CategoryGroup[] = [];

        for (const category of CATEGORIES) {
            const categoryResults = results.filter(r => r.testId.startsWith(category.prefix));
            if (categoryResults.length > 0) {
                groups.push({
                    name: category.name,
                    prefix: category.prefix,
                    results: categoryResults
                });
            }
        }

        // Handle any results that don't match known categories
        const knownPrefixes = CATEGORIES.map(c => c.prefix);
        const otherResults = results.filter(r => !knownPrefixes.some(p => r.testId.startsWith(p)));
        if (otherResults.length > 0) {
            groups.push({
                name: 'Other',
                prefix: 'OTHER-',
                results: otherResults
            });
        }

        return groups;
    }

    private getCategoryStatus(results: TestResult[]): { status: string; class: string } {
        const hasRunning = results.some(r => r.status === 'Running');
        const hasFailed = results.some(r => r.status === 'Failed');
        const allPassed = results.every(r => r.status === 'Passed');
        const allPending = results.every(r => r.status === 'Pending');
        const allSkipped = results.every(r => r.status === 'Skipped');

        if (hasRunning) return { status: 'Running', class: 'running' };
        if (hasFailed) return { status: 'Failed', class: 'failed' };
        if (allPassed) return { status: 'Passed', class: 'passed' };
        if (allPending) return { status: 'Pending', class: 'pending' };
        if (allSkipped) return { status: 'Skipped', class: 'skipped' };

        const passedCount = results.filter(r => r.status === 'Passed').length;
        return { status: `${passedCount}/${results.length} Passed`, class: 'partial' };
    }

    private updateResultsDisplay(results: TestResult[]): void {
        const container = document.getElementById('results-container');
        if (!container) return;

        const groups = this.groupResultsByCategory(results);

        container.innerHTML = '';

        for (const group of groups) {
            const categoryStatus = this.getCategoryStatus(group.results);
            const isExpanded = categoryStatus.status === 'Failed'
                || categoryStatus.status === 'Running'
                || this.expandedCategories.has(group.name);

            let skipReason = '';
            if (categoryStatus.status === 'Skipped') {
                const firstStep = [...group.results].sort((a, b) => a.testId.localeCompare(b.testId))[0];
                skipReason = firstStep?.errorMessage ?? '';
            }

            const categoryDiv = document.createElement('div');
            categoryDiv.className = 'cat-group';

            // ── Header row ──
            const catRow = document.createElement('div');
            catRow.className = `cat-row ${categoryStatus.class}`;
            catRow.innerHTML = `
                <span class="cat-name">${this.escapeHtml(group.name)}</span>
                ${skipReason ? `<span class="cat-skip-reason">${this.escapeHtml(skipReason)}</span>` : ''}
                <span class="cat-meta">
                    <span class="cat-step-count">${group.results.length} steps</span>
                    <span class="cat-status-badge">
                        ${categoryStatus.status === 'Running' ? '<span class="spinner"></span> ' : ''}${this.escapeHtml(categoryStatus.status)}
                    </span>
                    <span class="cat-chevron${isExpanded ? ' open' : ''}">&#9658;</span>
                </span>
            `;
            categoryDiv.appendChild(catRow);

            // ── Steps table ──
            const stepsWrap = document.createElement('div');
            stepsWrap.className = 'cat-steps-wrap';
            if (!isExpanded) stepsWrap.style.display = 'none';

            const table = document.createElement('table');
            table.className = 'steps-table';
            table.innerHTML = `
                <thead>
                    <tr>
                        <th>Step</th><th>Description</th><th>Status</th><th>Duration</th><th>Details</th>
                    </tr>
                </thead>
            `;
            const tbody = document.createElement('tbody');

            for (const result of group.results) {
                const row = document.createElement('tr');
                row.className = result.status.toLowerCase();

                const detailPairs = parseDetails(result.details);
                const detailsCell = result.status === 'Failed'
                    ? ''
                    : this.formatDetailChips(detailPairs);
                const duration = result.durationMs ? `${result.durationMs.toFixed(0)}ms` : '-';
                const rawDescription = result.description || '-';
                const emDashIdx = rawDescription.indexOf(' \u2014 ');
                const description = emDashIdx !== -1 ? rawDescription.slice(0, emDashIdx) : rawDescription;

                let statusDisplay = this.escapeHtml(result.status);
                if (result.status === 'Running') {
                    statusDisplay = `<span class="spinner"></span> ${statusDisplay}`;
                }

                row.innerHTML = `
                    <td>
                        <code class="step-id">${this.escapeHtml(result.testId)}</code>
                        <span class="step-name">${this.escapeHtml(result.name)}</span>
                    </td>
                    <td>${this.escapeHtml(description)}</td>
                    <td><span class="test-status ${result.status.toLowerCase()}">${statusDisplay}</span></td>
                    <td class="duration">${duration}</td>
                    <td class="details">${detailsCell}</td>
                `;

                tbody.appendChild(row);

                if (result.status === 'Failed') {
                    const errorSection = result.errorMessage
                        ? `<div class="step-detail-panel-section">
                            <span class="step-detail-panel-label">What failed</span>
                            <span>${this.escapeHtml(result.errorMessage)}</span>
                           </div>`
                        : '';
                    const detailsSection = detailPairs.length > 0
                        ? `<div class="step-detail-panel-section">
                            <span class="step-detail-panel-label">What was observed</span>
                            <span>${this.formatDetailChips(detailPairs)}</span>
                           </div>`
                        : '';
                    const specSection = this.renderSpecRef(result.testId);

                    if (errorSection || detailsSection || specSection) {
                        const panelRow = document.createElement('tr');
                        panelRow.className = 'step-detail-panel-row';
                        panelRow.innerHTML = `<td colspan="5"><div class="step-detail-panel">${errorSection}${detailsSection}${specSection}</div></td>`;
                        tbody.appendChild(panelRow);
                    }
                }
            }

            table.appendChild(tbody);
            stepsWrap.appendChild(table);
            categoryDiv.appendChild(stepsWrap);

            // ── Toggle handler ──
            catRow.addEventListener('click', () => {
                const open = stepsWrap.style.display !== 'none';
                stepsWrap.style.display = open ? 'none' : 'block';
                catRow.querySelector('.cat-chevron')?.classList.toggle('open', !open);
                if (open) {
                    this.expandedCategories.delete(group.name);
                } else {
                    this.expandedCategories.add(group.name);
                }
            });

            container.appendChild(categoryDiv);
        }
    }

    /**
     * Formats a parsed details array as HTML chip spans.
     * - Booleans (case-insensitive "true"/"false") render as ✓ true / ✗ false
     * - Empty string values render as — (U+2014)
     * - All other values are HTML-escaped and shown as-is
     * - Each pair is a <span class="detail-chip">; pairs are joined by " · "
     * Returns empty string when pairs is empty.
     */
    private formatDetailChips(pairs: Array<{ key: string; value: string }>): string {
        if (pairs.length === 0) return '';
        return pairs.map(({ key, value }) => {
            let display: string;
            if (value === '') {
                display = '\u2014'; // em-dash
            } else if (value.toLowerCase() === 'true') {
                display = '\u2713 true'; // ✓
            } else if (value.toLowerCase() === 'false') {
                display = '\u2717 false'; // ✗
            } else {
                display = this.escapeHtml(value);
            }
            return `<span class="detail-chip">${this.escapeHtml(key)}: ${display}</span>`;
        }).join(' \u00b7 '); // middle dot
    }

    /**
     * Returns the spec reference section HTML for a step, or '' if no entry exists.
     */
    private renderSpecRef(stepId: string): string {
        const ref = SPEC_REFS[stepId];
        if (!ref) return '';
        return `<div class="step-detail-panel-section">
        <span class="step-detail-panel-label">Spec reference</span>
        <a class="spec-ref" href="${this.escapeHtml(ref.url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(ref.spec)} \u2014 ${this.escapeHtml(ref.section)}</a>
    </div>`;
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

/**
 * Parses a semicolon-delimited key=value details string into structured pairs.
 * Splits on semicolons (with optional whitespace) to handle both "; " and ";" separators.
 * Splits each token on the first "=" only (preserves "=" in values).
 * Trims both key and value.
 * Drops tokens with no "=" character.
 * Returns empty array for null/undefined/empty input.
 */
function parseDetails(raw: string | null | undefined): Array<{ key: string; value: string }> {
    if (!raw) return [];
    return raw.split(/;\s*/)
        .map(token => {
            const idx = token.indexOf('=');
            if (idx === -1) return null;
            return { key: token.slice(0, idx).trim(), value: token.slice(idx + 1).trim() };
        })
        .filter((p): p is { key: string; value: string } => p !== null && p.key.length > 0);
}

// ── Browser-side WebRTC peer ─────────────────────────────────────────────────
//
// Speaks the ONVIF client signaling protocol over SignalingLibrary's /signaling/client
// endpoint (subprotocol: client.webrtc.onvif.org).
//
// Flow:
//   1. Connect to /signaling/client with subprotocol client.webrtc.onvif.org
//   2. Send JSON-RPC "register" → receive client ID
//   3. Poll /api/signaling/device-id until the camera has registered
//   4. Send JSON-RPC "connect" with the device ID → receive signaling session ID + ICE servers
//   5. Receive JSON-RPC "invite" (device's SDP offer) → create answer, reply
//   6. Exchange "trickle" ICE candidates
//   7. Report webrtc-connected and frame-report via separate /api/webrtc-browser/{id}/ws WS
class WebRtcBrowserPeer {
    private signalingWs: WebSocket | null = null;
    private statusWs: WebSocket | null = null;
    private pc: RTCPeerConnection | null = null;
    private pendingCandidates: RTCIceCandidateInit[] = [];
    private remoteDescSet = false;
    private signalingSessionId: string | null = null;
    private pendingRequests = new Map<string, (result: unknown) => void>();
    private reqCounter = 0;
    private framePoller: number | null = null;
    private frameSent = false;
    private stopped = false;

    constructor(private readonly sessionId: string) {}

    start(): void {
        const protocol = location.protocol === 'https:' ? 'wss' : 'ws';

        // Status-reporting WS: sends webrtc-connected and frame-report to the test runner
        this.statusWs = new WebSocket(`${protocol}://${location.host}/api/webrtc-browser/${this.sessionId}/ws`);
        this.statusWs.onopen  = () => console.log('[WebRTC] Status WS connected');
        this.statusWs.onerror = (e) => console.warn('[WebRTC] Status WS error', e);
        this.statusWs.onclose = () => console.log('[WebRTC] Status WS closed');

        // ONVIF signaling WS (SignalingLibrary client endpoint)
        this.signalingWs = new WebSocket(
            `${protocol}://${location.host}/signaling/client`,
            'client.webrtc.onvif.org');
        this.signalingWs.onopen    = () => { this.onSignalingOpen().catch(e => console.error('[WebRTC] onSignalingOpen error', e)); };
        this.signalingWs.onmessage = (e) => {
            try { this.onSignalingMessage(JSON.parse(e.data as string) as Record<string, unknown>); }
            catch(ex) { console.error('[WebRTC] Bad signaling message', ex); }
        };
        this.signalingWs.onerror = (e) => console.warn('[WebRTC] Signaling WS error', e);
        this.signalingWs.onclose = () => console.log('[WebRTC] Signaling WS closed');
    }

    stop(): void {
        this.stopped = true;
        if (this.framePoller !== null) { clearInterval(this.framePoller); this.framePoller = null; }
        if (this.pc) { this.pc.close(); this.pc = null; }
        if (this.signalingWs?.readyState === WebSocket.OPEN) this.signalingWs.close();
        if (this.statusWs?.readyState === WebSocket.OPEN) this.statusWs.close();
        this.signalingWs = null;
        this.statusWs = null;
    }

    // ── Signaling open: register then connect ─────────────────────────────────

    private async onSignalingOpen(): Promise<void> {
        console.log('[WebRTC] Signaling WS open, sending register...');
        const regResult = await this.request('register', { Authorization: 'none' }) as { Id: string };
        console.log('[WebRTC] Registered as client:', regResult.Id);
        await this.connectToDevice();
    }

    private async connectToDevice(): Promise<void> {
        // Poll the server until the camera has registered with the signaling server
        let deviceId: string | null = null;
        while (!deviceId && !this.stopped) {
            try {
                const resp = await fetch('/api/signaling/device-id');
                const data = await resp.json() as { deviceId: string | null };
                deviceId = data.deviceId;
            } catch { /* network hiccup — retry */ }
            if (!deviceId) await new Promise(r => setTimeout(r, 500));
        }
        if (!deviceId || this.stopped) return;

        console.log('[WebRTC] Device registered:', deviceId, '— sending connect...');
        const connResult = await this.request('connect', { Peer: deviceId, Authorization: 'none', Profile: null }) as {
            Session: string;
            IceServers: RTCIceServer[] | null;
        };
        this.signalingSessionId = connResult.Session;
        console.log('[WebRTC] Signaling session:', this.signalingSessionId);
        // RTCPeerConnection is created when the device sends its 'invite'
    }

    // ── Incoming signaling messages ───────────────────────────────────────────

    private onSignalingMessage(msg: Record<string, unknown>): void {
        const id = msg['id'] as string | undefined;

        // Response to one of our requests
        if (id && this.pendingRequests.has(id)) {
            const resolve = this.pendingRequests.get(id)!;
            this.pendingRequests.delete(id);
            resolve(msg['result']);
            return;
        }

        // Incoming request from the device (forwarded by SignalingLibrary)
        const method = msg['method'] as string | undefined;
        if (method === 'invite') {
            this.handleInvite(msg).catch(ex => console.error('[WebRTC] handleInvite error', ex));
        } else if (method === 'trickle') {
            this.handleTrickle(msg);
        }
    }

    // ── Invite: device sends its SDP offer ────────────────────────────────────

    private async handleInvite(msg: Record<string, unknown>): Promise<void> {
        const params = (msg['params'] as Record<string, unknown>) ?? {};
        // SignalingLibrary re-serializes with JsonSerializerDefaults.Web (camelCase)
        const sdp   = (params['offer']   ?? params['Offer'])   as string;
        const msgSession = (params['session'] ?? params['Session']) as string;
        const inviteId   = msg['id'] as string;

        console.log('[WebRTC] Received invite (device offer), creating answer');

        if (this.pc) { this.pc.close(); this.pc = null; }
        this.remoteDescSet = false;
        this.pendingCandidates = [];

        this.pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });

        this.pc.onicecandidate = (e) => {
            if (e.candidate && msgSession) {
                // Send trickle to SignalingLibrary — it forwards to the device
                this.sendSignaling({
                    jsonrpc: '2.0',
                    method: 'trickle',
                    params: { Session: msgSession, Candidate: e.candidate.toJSON() }
                });
            }
        };

        this.pc.onconnectionstatechange = () => {
            console.log('[WebRTC] Connection state:', this.pc?.connectionState);
            if (this.pc?.connectionState === 'connected') {
                this.sendStatus({ type: 'webrtc-connected' });
            }
        };

        this.pc.ontrack = (e) => {
            console.log('[WebRTC] Track received:', e.track.kind);
            if (e.track.kind === 'video' && e.streams.length > 0) {
                const videoEl = document.getElementById('webrtc-video') as HTMLVideoElement | null;
                if (videoEl) videoEl.srcObject = e.streams[0];
            }
            this.startFramePolling();
        };

        await this.pc.setRemoteDescription({ type: 'offer', sdp });
        this.remoteDescSet = true;

        // Drain candidates that arrived before remote description was set
        for (const c of this.pendingCandidates) {
            await this.pc.addIceCandidate(c).catch(() => {});
        }
        this.pendingCandidates = [];

        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);

        // Reply to the invite — SignalingLibrary matches by ID and forwards to the device
        this.sendSignaling({
            jsonrpc: '2.0',
            id: inviteId,
            result: { Answer: answer.sdp, Subprotocols: [] }
        });
        console.log('[WebRTC] Sent invite answer');
    }

    // ── Trickle: device ICE candidate ─────────────────────────────────────────

    private handleTrickle(msg: Record<string, unknown>): void {
        const params = (msg['params'] as Record<string, unknown>) ?? {};
        // Camera sends PascalCase; SignalingLibrary forwards as-is
        const candidate = (params['Candidate'] ?? params['candidate']) as RTCIceCandidateInit | undefined;
        if (!candidate) return;

        if (this.remoteDescSet && this.pc) {
            this.pc.addIceCandidate(candidate).catch(() => {});
        } else {
            this.pendingCandidates.push(candidate);
        }
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    private request(method: string, params: object): Promise<unknown> {
        const id = `r${++this.reqCounter}`;
        const promise = new Promise<unknown>(resolve => { this.pendingRequests.set(id, resolve); });
        this.sendSignaling({ jsonrpc: '2.0', id, method, params });
        return promise;
    }

    private sendSignaling(msg: object): void {
        if (this.signalingWs?.readyState === WebSocket.OPEN)
            this.signalingWs.send(JSON.stringify(msg));
    }

    private sendStatus(msg: object): void {
        if (this.statusWs?.readyState === WebSocket.OPEN)
            this.statusWs.send(JSON.stringify(msg));
    }

    private startFramePolling(): void {
        if (this.framePoller !== null) return;
        this.framePoller = window.setInterval(() => { this.checkFrames().catch(() => {}); }, 500);
    }

    private async checkFrames(): Promise<void> {
        if (!this.pc || this.frameSent) return;
        const stats = await this.pc.getStats();
        stats.forEach((report) => {
            const r = report as RTCStatsReport & Record<string, unknown>;
            if (r['type'] === 'inbound-rtp' && r['kind'] === 'video') {
                const framesReceived = (r['framesReceived'] as number) || 0;
                if (framesReceived > 0 && !this.frameSent) {
                    this.frameSent = true;
                    this.sendStatus({ type: 'frame-report', framesReceived });
                    if (this.framePoller !== null) { clearInterval(this.framePoller); this.framePoller = null; }
                    console.log(`[WebRTC] Reported ${framesReceived} frames`);
                }
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new ConformanceApp();
});
